import {customElement, property, state} from 'lit/decorators.js';
import {html, LitElement, PropertyValues} from 'lit';
import '@/components/datepicker/datepicker-timeframe'
import '@/components/datepicker/datepicker-calendar'
import '@/components/datepicker/datepicker-navigation'
import {datepickerStyles} from '@/components/datepicker/datepicker.styles';
import {weekday, languageObject, placeholder} from './datepicker.language';
import {
    Validator,
    RequiredValidator,
    DateValidator,
    WorkdayValidator,
    BirthdayValidator,
    CallBackDayValidator,
    BonusprogramDateValidator,
    BirthdayOgsValidator,
    FutureDateValidator,
    PastDateValidator,
    BirthdayMaxAgeValidator,
    DatePlausibilityValidator, FromDateValidator, ToDateValidator, BirthdayWstValidator
} from "@/components/validators/validators";
import {query} from 'lit/decorators/query.js';
import {FormControlDependencyMixin} from "@/components/form-control-dependency/form-control-dependency-mixin";
import {SbkLabelMixin} from "@/components/label/label-mixin";
import {FormValidationMixin} from '../mixins/form-validation-mixin';
import {when} from 'lit/directives/when.js';
import {labelStyles} from '../label/label.styles';
import {inputStyles} from "@/components/input/input.styles.ts";
import {DatepickerTimeframe} from "@/components/datepicker/datepicker-timeframe.ts";
import {DatepickerCalendar} from "@/components/datepicker/datepicker-calendar.ts";
import {DatepickerNavigation} from "@/components/datepicker/datepicker-navigation.ts";
import './datepicker.scss'
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import {SbkRoundButton} from "@/components/round-button/round-button.ts";
import * as helpers from "@/components/validators/validators.helper.ts";

@customElement('sbk-datepicker')
export class SbkDatepicker extends FormControlDependencyMixin(FormValidationMixin(SbkLabelMixin(LitElement))) {
    static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

    @property({reflect: true, attribute: 'datepicker-value'}) datepickerValue = '';
    @property({type: Boolean}) disabled: boolean = false;
    @property({reflect: true}) name: string = '';
    @property({type: Boolean}) readonly: boolean = false;
    @property({type: Boolean}) required: boolean = false;
    @property({type: Boolean}) hidePopup: boolean = false;
    @property({type: Array}) daysInMonth: { day: number, disabled: boolean }[] = [];
    @property({type: Number}) selectedYear: number | null = null;
    @property({type: Number}) selectedMonth: number | null = null;
    selectedYearInDatepicker = new Date().getFullYear();
    selectedMonthInDatepicker = new Date().getMonth() + 1;
    @property({type: Number}) selectedDay: number | null = null;
    @property({type: String}) minDate: string = '1915-01-01';
    @property({type: String}) maxDate: string = '2035-12-31';
    @property({type: Boolean}) disableWeekendDays: boolean = false;
    @property({type: String}) disabledDays: string = '';
    @property({type: Boolean}) allowSelectingDaysInPrevAndNextMonths: boolean = true;
    @property({type: Array}) weekDays: string[] = this.setLanguage(weekday);
    @property() description: string = '';
    @property() placeholder: string = this.setLanguage(placeholder)[0];
    @state() isOpen: boolean = false;
    @state() renderAbove: boolean = false;
    @state() datepickerHeight: number = 410;

    @query('datepicker-timeframe') timeframe!: DatepickerTimeframe;
    @query('datepicker-calendar') calendar: DatepickerCalendar|undefined;
    @query('datepicker-navigation') navigation!: DatepickerNavigation;
    @query('.datepicker-icon') iconButton!: SbkRoundButton;

    preventFocusOut: boolean = false;
    touchStart: number | null = null;

    private _touched = false;
    private _focusedByClick = false;

    validatorMapper: {
        [key: string]: Validator
    } = {
        'required': RequiredValidator,
        'validateDate': DateValidator,
        'validateBirthday': BirthdayValidator,
        'validateWorkday': WorkdayValidator,
        'validateCallBackDay': CallBackDayValidator,
        'validateBonusprogramDate': BonusprogramDateValidator,
        'validateBirthdayOgs': BirthdayOgsValidator,
        'validateFutureDate': FutureDateValidator,
        'validatePastDate': PastDateValidator,
        'validateBirthdayMaxAge': BirthdayMaxAgeValidator,
        'validateDatePlausibility': DatePlausibilityValidator,
        'validateFromDate': FromDateValidator,
        'validateToDate': ToDateValidator,
        'validateBirthdayWst': BirthdayWstValidator,
    }

    static formAssociated = true;
    _internals: ElementInternals;

    static styles = [
        labelStyles,
        inputStyles,
        datepickerStyles
    ];

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
        });
        this.addEventListener('form-control-dependency-hide', (e: Event) => {
            this.hide();
            this.resetValue((e as CustomEvent).detail.isInitialCall);
        });
        this.addEventListener('form-control-dependency-required', () => {
            this._required();
        });
        this.addEventListener('form-control-dependency-optional', () => {
            this.optional();
        });
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDatepickerOpen = this.handleDatepickerOpen.bind(this) as EventListener;
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this._onMouseDown = this._onMouseDown.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.datepickerValue) {
            this.datepickerValue = this.transformToGermanDateFormat(this.datepickerValue);
            const value = this.transformDate(this.datepickerValue);
            this._internals.setFormValue(value);
            this.selectedDay = new Date(value).getDate();
            this.selectedMonth = new Date(value).getMonth() + 1;
            this.selectedYear = new Date(value).getFullYear();
        }
        if (new Date(this.maxDate).getFullYear() < this.selectedYearInDatepicker) {
            this.selectedMonthInDatepicker = new Date(this.maxDate).getMonth() + 1;
            this.selectedYearInDatepicker = new Date(this.maxDate).getFullYear();
        }
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('datepicker-open', this.handleDatepickerOpen);
        this.addEventListener('keydown', this.handleKeyDown);
        this.addEventListener('focusout', this.handleFocusOut);
        this.addEventListener('mousedown', this._onMouseDown);
        this.addEventListener('touchstart', this.handleTouchStart);
        this.addEventListener('touchmove', this.handleTouchMove);
        this.addEventListener('touchend', this.handleTouchEnd);

        const openEvent = new CustomEvent('datepicker-open', {
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(openEvent);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('datepicker-open', this.handleDatepickerOpen);
        this.removeEventListener('keydown', this.handleKeyDown);
        this.removeEventListener('focusout', this.handleFocusOut);
        this.removeEventListener('mousedown', this._onMouseDown);
        this.removeEventListener('touchstart', this.handleTouchStart);
        this.removeEventListener('touchmove', this.handleTouchMove);
        this.removeEventListener('touchend', this.handleTouchEnd);
    }

    handleDatepickerOpen(event: Event) {
        if (event instanceof CustomEvent && event.target !== this) {
            this.closeDatepicker();
        }
    }

    willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('datepickerValue')) {
            const isValid = DateValidator.isValid(this.datepickerValue);
            if (isValid) {
                this._internals.setFormValue(this.transformDate(this.datepickerValue));
            }
            const changeEvent = new CustomEvent('datepicker-change', {
                detail: { value: this.datepickerValue },
                bubbles: true,
                composed: true
            });
            this.dispatchEvent(changeEvent);
        }
    }

    setLanguage(element: languageObject) {
        const lang = document.documentElement.lang;
        return lang.includes('de') ? element.German : element.English;
    }

    render() {
        return html`
            <div
                class="input__wrapper${this.invalid ? ' input__wrapper--error' : ''}${this.disabled || this.readonly ? ' input__wrapper--disabled' : ''}">
                ${this.labelTemplate(`${this.name}-input`, false, this.invalid)}
                ${when(this.description,
                    () => html`
                            <sbk-annotation id="${this.name}-annotation" variant="info" ?disabled=${this.disabled || this.readonly}>${unsafeHTML(this.description)}</sbk-annotation>
                        `)}
                <div class="input__container">
                    <input
                        id="${this.name}-input"
                        class="input input--large"
                        ?disabled=${this.disabled}
                        ?readonly=${this.readonly}
                        ?required=${this.required}
                        .value=${this.datepickerValue}
                        @input=${this.handleInput}
                        @keydown=${this.handleInputKeyDown}
                        @blur=${this._validate}
                        tabindex="0"
                        placeholder=${this.placeholder}
                        aria-haspopup="dialog"
                        aria-expanded=${this.isOpen}
                        aria-controls="datepicker-dialog"></input>
                    ${this.renderDatepickerIcon()}
                    ${this.isOpen ? this.renderDatePicker() : ''}
                </div>
                ${this.invalid ? this.validationErrorTemplate("annotation") : ""}
            </div>
        `;
    }

    renderDatepickerIcon() {
        if (this.hidePopup) {
            return '';
        }
        return html`
            <sbk-round-button icon="calender" variant="icon-only" size="small" class="datepicker-icon" @click=${this.toggleDatepicker}></sbk-round-button>
        `;
    }

    renderDatePicker() {
        return html`
            <div
                class="datepicker ${this.hidePopup ? 'input--only' : ''} ${this.renderAbove ? 'datepicker__above' : ''}"
                id="datepicker-dialog" @click=${(event: Event) => event.stopPropagation()} role="dialog"
                aria-modal="true" part="datepicker-dialog">
                <datepicker-timeframe
                    minDate=${this.minDate}
                    maxDate=${this.maxDate}
                    selectedMonth=${this.selectedMonthInDatepicker}
                    selectedYear=${this.selectedYearInDatepicker}
                    @select-changed=${this.handleChange}
                    @tab-keydown=${this.handleTabFromTimeframe}></datepicker-timeframe>
                <datepicker-calendar
                    minDate=${this.minDate}
                    maxDate=${this.maxDate}
                    selectedDay=${this.selectedDay}
                    selectedMonth=${this.selectedMonth}
                    selectedYear=${this.selectedYear}
                    ?disableWeekendDays=${this.disableWeekendDays}
                    disabledDays=${this.disabledDays}
                    @date-selected=${this.selectDate}
                    @tab-keydown=${this.handleTabKey}></datepicker-calendar>
                <datepicker-navigation
                    .prevButtonDisabled=${this.isMonthDisabled(this.selectedMonthInDatepicker - 1)}
                    .nextButtonDisabled=${this.isMonthDisabled(this.selectedMonthInDatepicker + 1)}
                    @navigation-changed=${this.handleNavigation}
                    @navigation-tab-keydown=${this.handleNavigationTabKeydown}
                ></datepicker-navigation>
            </div>
        `;
    }

    transformDate(date: string): string {
        if (!date) {
            return '';
        }
        const [day, month, year] = date.split('.');
        return `${year}-${month}-${day}`;
    }

    transformToGermanDateFormat(date: string): string {
        const [year, month, day] = date.split('-');
        return `${day}.${month}.${year}`;
    }


    updateSelectedDateValues(day: number | null, month: number, year: number) {
        this.selectedDay = day;
        this.selectedMonth = month;
        this.selectedYear = year;
        this.selectedMonthInDatepicker = month;
        this.selectedYearInDatepicker = year;
        this.updateDatepickerValue();
    }

    handleInput(event: Event) {
        this._touched = true;
        const element = (<HTMLInputElement>event.target);
        const dateStr = element?.value.replace(/[^0-9.]/g, '');
        element.value = dateStr;
        if (!dateStr) {
            this.datepickerValue = '';
            return;
        }
        const isValid = DateValidator.isValid(dateStr);
        if (!isValid) {
            this.datepickerValue = dateStr;
            return;
        }
        const minDate = new Date(this.minDate);
        const maxDate = new Date(this.maxDate);
        const givenDate = helpers.getFormattedSelectedDate(dateStr);

        if (givenDate) {
            const givenTime = givenDate.getTime();
            const [day, month, year] = dateStr.split('.').map(Number);
            if (!this.isDayDisabled(day, month, year) && minDate && givenTime >= minDate.getTime() && maxDate && givenTime <= maxDate.getTime()) {
                this.updateSelectedDateValues(day, month, year);
            } else {
                this.selectedDay = null;
                this.selectedMonth = this.selectedMonthInDatepicker;
                this.selectedYear = this.selectedYearInDatepicker;
            }
        }
        this.datepickerValue = dateStr;
    }

    _onMouseDown() {
        this._focusedByClick = true;
    }

    _validate(event: Event) {
        if (event instanceof FocusEvent) {
            const relatedTarget = event.relatedTarget as Node;
            if (this.shadowRoot?.contains(relatedTarget)) {
                return;
            }
        }
        const element = <HTMLInputElement>event.target;
        // Only validate if clicked, or if field has been touched and filled
        if (!this._focusedByClick && (!this._touched || !element.value)) {
            return;
        }

        this._focusedByClick = false; // Reset for next blur
        const validators = this.getValidators();
        this.handleErrors(validators, element.value);
        this.dispatchEvent(new CustomEvent('input-blur'));
    }

    handleInputKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            const element = <HTMLInputElement>event.target;
            this.datepickerValue = element.value;
            this._internals.setFormValue(this.transformDate(this.datepickerValue));
            const submitButton: HTMLInputElement | null | undefined = this.closest('form')?.querySelector('input[type="submit"]')
            this._internals.form?.requestSubmit(submitButton);
        } else if (event.key === 'Escape') {
            this.closeDatepicker();
        }
    }

    handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.closeDatepicker();
        }
    }

    handleTabFromTimeframe() {
        this.calendar && this.calendar.focus();
    }

    handleNavigation(event: CustomEvent) {
        this.changeMonth(event.detail.increment);
    }

    handleNavigationTabKeydown(event: CustomEvent) {
        if (event.detail === 'prev') {
            this.calendar && this.calendar.focus();
        } else {
            this.timeframe && this.timeframe.focus();
        }
    }

    changeMonth(monthIncrement: number) {
        this.preventFocusOut = true;
        const month = this.selectedMonthInDatepicker + monthIncrement;

        if (month > 12) {
            this.selectedMonthInDatepicker = 1;
            this.selectedYearInDatepicker++;
        } else if (month < 1) {
            this.selectedMonthInDatepicker = 12;
            this.selectedYearInDatepicker--;
        } else {
            this.selectedMonthInDatepicker += monthIncrement
        }
        this.timeframe.selectedMonth = this.selectedMonthInDatepicker;
        this.timeframe.selectedYear = this.selectedYearInDatepicker;
        if (this.calendar) {
            this.calendar.selectedTimeframe = {month: this.selectedMonthInDatepicker, year: this.selectedYearInDatepicker};
        }
        this.setNavigationButtons();
    }

    selectDate(event: CustomEvent) {
        this.setDay(event.detail.day, event.detail.month, event.detail.year)
    }

    setDay(day: number, month: number = this.selectedMonthInDatepicker, year: number = this.selectedYearInDatepicker) {
        if (!this.isDayDisabled(day, month)) {
            this.selectedDay = day;
            if (month !== this.selectedMonthInDatepicker) {
                this.selectedMonthInDatepicker = month;
            }
            if (year !== this.selectedYearInDatepicker) {
                this.selectedYearInDatepicker = year;
            }
            this.updateDatepickerValue();
            this.closeDatepicker();
        }
    }

    isDayDisabled(day: number, month: number = this.selectedMonthInDatepicker, year: number = this.selectedYearInDatepicker): boolean {
        const date = new Date(year, month - 1, day);
        const minDate = new Date(this.minDate);
        const min = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
        const max = new Date(this.maxDate);
        const isWeekendDisabled = this.disableWeekendDays && (date.getDay() === 0 || date.getDay() === 6); // Sunday = 0 and Saturday = 6
        let isInDisabledDays = false;
        const disabledTimestampArray = this.disabledDays.split('|');
        for (const timestamp of disabledTimestampArray) {
            const disabledDay = new Date(parseInt(timestamp) * 1000); // convert from seconds to milliseconds
            if (`${day}.${month}` === `${disabledDay.getDate()}.${disabledDay.getMonth() + 1}`) {
                isInDisabledDays = true;
                break;
            }
        }
        return date < min || date > max || isWeekendDisabled || isInDisabledDays;
    }

    isMonthDisabled(month: number, year: number = this.selectedYearInDatepicker): boolean {
        const firstDayOfMonth = new Date(year, month - 1, 1);
        const lastDayOfMonth = new Date(year, month, 0);

        const minDate = new Date(this.minDate);
        const min = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
        const max = new Date(this.maxDate);

        const isOverlapped = (firstDayOfMonth >= min && firstDayOfMonth <= max) || (lastDayOfMonth >= min && lastDayOfMonth <= max);

        return !isOverlapped;
    }

    handleTabKey(event: CustomEvent) {
        event.preventDefault();
        const focusTarget = event.detail.shiftKey
            ? this.timeframe
            : this.navigation
        focusTarget?.focus();
    }

    handleDocumentClick = (event: MouseEvent) => {
        if (this.hidePopup) return
        const path = event.composedPath();
        if (!path.includes(this)) {
            this.closeDatepicker();
        }
    }

    handleFocusOut = (event: FocusEvent) => {
        const relatedTarget = event.relatedTarget as Node;
        if (!this.shadowRoot?.contains(relatedTarget)) {
            if (!this.preventFocusOut) {
                this.closeDatepicker();
            }
            this.preventFocusOut = false;
        }
    }

    handleTouchStart(event: TouchEvent) {
        this.preventFocusOut = true;
        if (this.isOpen) {
            const swipeArea = this.shadowRoot!.querySelector('datepicker-calendar')!.shadowRoot!.querySelector('table') as HTMLElement;
            const firstTouch = event.touches[0];
            if (swipeArea && this.isInArea(firstTouch, swipeArea.getBoundingClientRect())) {
                this.touchStart = firstTouch.screenX;
                swipeArea.style.cssText = '';
                swipeArea.classList.remove('slide-in-left', 'slide-in-right');
            }
        }
    }

    handleTouchMove(event: TouchEvent) {
        this.preventFocusOut = true;
        if (!this.touchStart) {
            return;
        }
        if (this.isOpen) {
            const swipeArea = this.shadowRoot!.querySelector('datepicker-calendar')!.shadowRoot!.querySelector('table') as HTMLElement;
            const currentX = event.touches[0].screenX;
            const diff = currentX - this.touchStart;
            if (swipeArea && ((diff > 0 && this.isMonthDisabled(this.selectedMonthInDatepicker + 1))
                || (diff < 0 && this.isMonthDisabled(this.selectedMonthInDatepicker - 1)))) {
                swipeArea.style.transform = `translateX(${diff}px)`;
                swipeArea.style.opacity = `${1 - Math.abs(diff) / swipeArea.offsetWidth}`;
            }
        }
    }

    handleTouchEnd(event: TouchEvent) {
        this.preventFocusOut = true;
        if (!this.touchStart) {
            return;
        }

        if (this.isOpen) {
            const swipeArea = this.shadowRoot!.querySelector('datepicker-calendar')!.shadowRoot!.querySelector('table') as HTMLElement;
            if (swipeArea) {
                swipeArea.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
                const currentTouch = event.changedTouches[0].screenX;
                const diff = this.touchStart - currentTouch;

                if (Math.abs(diff) > 50) {
                    if (diff > 0) {
                        // Swipe left
                        swipeArea.classList.add('slide-out-left');
                        if (!this.isMonthDisabled(this.selectedMonthInDatepicker + 1)) {
                            this.changeMonth(1);
                        }
                    } else {
                        // Swipe right
                        swipeArea.classList.add('slide-out-right');
                        if (!this.isMonthDisabled(this.selectedMonthInDatepicker - 1)) {
                            this.changeMonth(-1);
                        }
                    }

                    setTimeout(() => {
                        this.resetSwipe(swipeArea);
                    }, 300);
                } else {
                    this.resetSwipe(swipeArea);
                }
            }
        }
    }

    isInArea(touch: Touch, area: ClientRect): boolean {
        const touchX = touch.clientX;
        const touchY = touch.clientY;
        return touchX >= area.left &&
            touchX <= area.right &&
            touchY >= area.top &&
            touchY <= area.bottom;
    }

    resetSwipe(swipeArea: HTMLElement): void {
        swipeArea.classList.remove('slide-out-left', 'slide-out-right');
        swipeArea.classList.add('slide-in-left', 'slide-in-right');
        swipeArea.style.transform = 'translateX(0)';
        swipeArea.style.opacity = '1';
        this.touchStart = null;
    }

    handleChange(event: CustomEvent) {
        this.preventFocusOut = true;
        this.selectedMonthInDatepicker = event.detail.month;
        this.selectedYearInDatepicker = event.detail.year;
        if (this.calendar) {
            this.calendar.selectedTimeframe = {month: event.detail.month, year: event.detail.year};
        }
        this.setNavigationButtons();
    }

    setNavigationButtons() {
        this.navigation.prevButtonDisabled = this.isMonthDisabled(this.selectedMonthInDatepicker - 1);
        this.navigation.nextButtonDisabled = this.isMonthDisabled(this.selectedMonthInDatepicker + 1);
    }

    toggleDatepicker(event: Event) {
        if (this.disabled || this.readonly) {
            return;
        }
        event.stopPropagation();
        if (this.hidePopup) return;

        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            this.preventFocusOut = true;
            this.updateComplete.then(() => {
                if (this.selectedMonth && this.selectedYear) {
                    this.updateSelectedDateValues(this.selectedDay, this.selectedMonth, this.selectedYear);
                } else {
                    this.updateSelectedDateValues(this.selectedDay, this.selectedMonthInDatepicker, this.selectedYearInDatepicker);
                }
                this.calendar && this.calendar.focus();

                const openEvent = new CustomEvent('datepicker-open', {
                    bubbles: true,
                    composed: true
                });
                this.dispatchEvent(openEvent);
            });
        }
        this.renderAbove = !this.isEnoughSpaceBelow();
    }

    closeDatepicker() {
        if (this.isOpen) {
            this.isOpen = false;
            if (this.iconButton) {
                this.iconButton.focus();
            }
        }
    }

    updateDatepickerValue() {
        if (this.selectedDay === null || this.selectedMonthInDatepicker === null || this.selectedYearInDatepicker === null) {
            return;
        }

        let month = this.selectedMonthInDatepicker;
        let year = this.selectedYearInDatepicker;
        const day = this.selectedDay;

        if (month === 0) {
            month = 12;
            this.selectedMonthInDatepicker = month;
            this.selectedYearInDatepicker = this.selectedYearInDatepicker - 1;
            year = year -1
        } else if (month === 13) {
            month = 1;
            this.selectedYearInDatepicker = this.selectedYearInDatepicker + 1;
            year = year + 1
        }
        this.datepickerValue = `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`;
        this.selectedMonth = month;
        this.selectedYear = year;
        if (!this.hidePopup && this.calendar) {
            this.calendar.selectedTimeframe = {month: this.selectedMonth, year: this.selectedYear};
        }

        this.validate();
        this._internals.setFormValue(this.transformDate(this.datepickerValue));
    }

    validate() {
        const validators = this.getValidators();
        this.handleErrors(validators, this.datepickerValue);
    }

    hide(): void {
        this.hidden = true;
        this.invalid = false;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(isInitialCall: boolean = false): void {
        if (!isInitialCall && (!this.readonly && !this.disabled)) {
            this.datepickerValue = '';
            this.selectedYear = null;
            this.selectedMonth = null;
            this.selectedYearInDatepicker = new Date().getFullYear();
            this.selectedMonthInDatepicker = new Date().getMonth() + 1;
            this.selectedDay = null;
        }
        this._internals.setFormValue('');
    }

    setValue(): void {
        this._internals.setFormValue(this.transformDate(this.datepickerValue));
    }

    isEnoughSpaceBelow(): boolean {
        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        return !(spaceBelow < this.datepickerHeight && spaceAbove > this.datepickerHeight);
    }

    _required(): void {
        this.required = true;
    }

    optional(): void {
        this.required = false;
    }


}
